import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { supabase } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import Avatar from "../components/common/Avatar";
import LoadingOverlay from "../components/LoadingOverlay";

function formatTime(isoString) {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "now";
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d`;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function ChatsListPage() {
  const { user } = useAuth();
  const location = useLocation();
  const [tab, setTab] = useState("contributing");
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) return;

    async function fetchConversations() {
      setLoading(true);
      try {
        // 1. Fetch user's own posts + responses where user is responder (parallel)
        const [postsResult, responderResult] = await Promise.all([
          supabase
            .from("posts")
            .select("id, title, author_id")
            .eq("author_id", user.id),
          supabase
            .from("responses")
            .select("id, post_id, responder_id, status, completed_at, created_at")
            .eq("responder_id", user.id)
            .eq("status", "accepted"),
        ]);

        const userPosts = postsResult.data;
        const asResponder = responderResult.data;

        const postMap = {};
        (userPosts || []).forEach((p) => (postMap[p.id] = p));
        const myPostIds = Object.keys(postMap);

        // 2. Fetch responses to user's own posts
        let asAuthor = [];
        if (myPostIds.length > 0) {
          const { data } = await supabase
            .from("responses")
            .select("id, post_id, responder_id, status, completed_at, created_at")
            .in("post_id", myPostIds)
            .eq("status", "accepted");
          asAuthor = data || [];
        }

        // 3. Combine and deduplicate, active only
        const responseMap = {};
        [...(asResponder || []), ...asAuthor].forEach((r) => {
          if (!r.completed_at) responseMap[r.id] = r;
        });

        // 4. Tab filter: requesting = user is post author, contributing = user is responder
        const allResponses = Object.values(responseMap).filter((r) => {
          const post = postMap[r.post_id];
          if (!post) return false;
          return tab === "requesting"
            ? post.author_id === user.id
            : r.responder_id === user.id;
        });

        if (allResponses.length === 0) {
          setConversations([]);
          return;
        }

        // 5. Fetch missing post info (posts user responded to, not their own)
        const missingPostIds = [
          ...new Set(
            allResponses.map((r) => r.post_id).filter((pid) => !postMap[pid])
          ),
        ];
        if (missingPostIds.length > 0) {
          const { data: otherPosts } = await supabase
            .from("posts")
            .select("id, title, author_id")
            .in("id", missingPostIds);
          (otherPosts || []).forEach((p) => (postMap[p.id] = p));
        }

        // 6. Determine partners and batch-fetch user info
        const partnerIds = [
          ...new Set(
            allResponses.map((r) => {
              const post = postMap[r.post_id];
              return user.id === post?.author_id
                ? r.responder_id
                : post?.author_id;
            })
          ),
        ].filter(Boolean);

        const userMap = {};
        if (partnerIds.length > 0) {
          const { data: users } = await supabase
            .from("users")
            .select("id, display_name, avatar_url, is_seed")
            .in("id", partnerIds);
          (users || []).forEach((u) => (userMap[u.id] = u));
        }

        // 7. Fetch latest message per conversation
        const responseIds = allResponses.map((r) => r.id);
        const messageMap = {};
        if (responseIds.length > 0) {
          const { data: messages } = await supabase
            .from("chat_messages")
            .select("response_id, body, created_at")
            .in("response_id", responseIds)
            .order("created_at", { ascending: false });
          (messages || []).forEach((m) => {
            if (!messageMap[m.response_id]) {
              messageMap[m.response_id] = m;
            }
          });
        }

        // 8. Assemble and sort
        const assembled = allResponses
          .filter((r) => postMap[r.post_id])
          .map((r) => {
            const post = postMap[r.post_id];
            const partnerId =
              user.id === post.author_id ? r.responder_id : post.author_id;
            const partner = userMap[partnerId] || {
              display_name: "Unknown",
              avatar_url: null,
            };
            const lastMsg = messageMap[r.id];
            return {
              responseId: r.id,
              postTitle: post.title,
              partner,
              lastMessageBody: lastMsg?.body || null,
              sortTime: lastMsg?.created_at || r.created_at,
            };
          });

        assembled.sort((a, b) => {
          const aSeed = a.partner.is_seed ? 1 : 0;
          const bSeed = b.partner.is_seed ? 1 : 0;
          if (aSeed !== bSeed) return bSeed - aSeed;
          return new Date(b.sortTime) - new Date(a.sortTime);
        });
        setConversations(assembled);
      } catch (err) {
        console.error("ChatsListPage fetch error:", err);
        setConversations([]);
      } finally {
        setLoading(false);
      }
    }

    fetchConversations();
  }, [user?.id, tab, location.key]);

  return (
    <div>
      <div className="flex gap-1 mb-6 bg-cream rounded-lg p-1">
        {[
          { key: "contributing", label: "Contributing" },
          { key: "requesting", label: "Requesting" },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              tab === key
                ? "bg-terracotta text-white"
                : "text-walnut-light hover:text-walnut"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {loading && <LoadingOverlay />}

      {!loading && conversations.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <MessageCircle size={48} className="text-stone mb-4" />
          <p className="text-walnut font-medium mb-1">
            No {tab === "contributing" ? "contributing" : "requesting"} conversations yet.
          </p>
          <p className="text-sm text-walnut-light">
            When you accept or get accepted on a post, your chats will appear
            here.
          </p>
        </div>
      )}

      {!loading && conversations.length > 0 && (
        <div className="space-y-3">
          {conversations.map((c) => (
            <Link
              key={c.responseId}
              to={`/chat/${c.responseId}`}
              className="flex items-center gap-3 p-4 bg-cream rounded-lg shadow-card hover:shadow-card-hover transition-shadow"
            >
              <Avatar user={c.partner} size="md" />
              <div className="flex-1 min-w-0">
                <p className="font-display font-semibold text-walnut truncate">
                  {c.postTitle}
                </p>
                <p className="text-sm text-walnut-light">
                  with {c.partner.display_name}
                </p>
                {c.lastMessageBody && (
                  <p className="text-sm text-walnut-light/70 truncate">
                    {c.lastMessageBody}
                  </p>
                )}
              </div>
              <span className="text-xs text-walnut-light whitespace-nowrap self-start mt-1">
                {formatTime(c.sortTime)}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
